import type { ExperimentOptionComparisonEvidence } from '@einhaus/project314-types';
import { OptionOutcomeCompletion, formatOptionOutcomeCompletion } from '@/components/Experiments/OptionOutcomeCompletion';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import DataTable from '@/components/UI/DataTable';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { Button, Card, Badge, FormInput } from '@/components/UI';
import { FaArrowLeft, FaDownload, FaFilter, FaList, FaSearch } from 'react-icons/fa';
import dayjs from 'dayjs';
import { getEntityLink } from '@/utils/entityHelpers';
import CreateEntityListModal from '@/components/Experiments/CreateEntityListModal';
import CreateUniverseScreenerModal from '@/components/Experiments/CreateUniverseScreenerModal';
import { computeEdgeEligibleCount, EdgeCoverageCell } from '@/components/Experiments/EdgeCoverageCell';
import { formatTimeHorizon, getEventHorizonTooltip, isEventHorizon, sortHorizons } from '@/utils/timeHorizonGenerator';

interface EntityMetrics {
    // Stock metrics
    avgProfitPercent: number | null;
    avgSpyProfitPercent: number | null;
    avgAlphaPercent: number | null;
    winRate: number | null;
    winRateVsSpy: number | null;
    avgMaxProfitPercent: number | null;
    avgMaxLossPercent: number | null;
    profitableEntries: number;
    totalEntries: number;
    bestProfit: number | null;
    worstLoss: number | null;

    // Random-entry baseline timing edge (null = baseline pending)
    avgEntryEdgePercent?: number | null;
    entryEdgeCoveredCount?: number;
    entryEdgeCoveredEmptyCount?: number;
    entryEdgeMissingCount?: number;

    // Option metrics (only populated when includeOptions is true)
    optionAvgProfitPercent?: number | null;
    avgOptionEntryEdgePercent?: number | null;
    optionEntryEdgeCoveredCount?: number;
    optionEntryEdgeCoveredEmptyCount?: number;
    optionEntryEdgeMissingCount?: number;
    optionWinRate?: number | null;
    optionAvgMaxProfitPercent?: number | null;
    optionAvgMaxLossPercent?: number | null;
    optionProfitableEntries?: number;
    optionTotalEntries?: number;

    // Comparison metrics
    optionOutperformanceRate?: number | null;
    optionAvgLeverage?: number | null;
    optionSuccessRate?: number | null;
    optionEvidence?: ExperimentOptionComparisonEvidence;
}

interface EntityResult {
    entityId: number;
    entityType: 'Asset' | 'AssetIndex' | 'Cryptocurrency';
    symbol: string;
    durations: string[];
    totalEntries: number;
    overallMetrics: EntityMetrics;
    horizonMetrics: Record<string, EntityMetrics>;
}

function getExecutionStatusBadgeVariant(
    status: 'draft' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'deleting'
): React.ComponentProps<typeof Badge>['variant'] {
    switch (status) {
        case 'completed':
            return 'success';
        case 'queued':
        case 'draft':
        case 'cancelled':
        case 'deleting':
            return 'secondary';
        case 'running':
            return 'warning';
        case 'failed':
            return 'danger';
    }
}

function getDiscoveryStatusBadgeVariant(
    status: 'disabled' | 'not_started' | 'not_applicable' | 'preparing_objective' | 'pending' | 'running' | 'completed' | 'failed'
): React.ComponentProps<typeof Badge>['variant'] {
    switch (status) {
        case 'completed':
            return 'success';
        case 'pending':
            return 'secondary';
        case 'running':
            return 'warning';
        case 'failed':
            return 'danger';
        case 'preparing_objective':
        case 'disabled':
        case 'not_started':
        case 'not_applicable':
            return 'secondary';
    }
}

const ExperimentEntityResults: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const experimentId = parseInt(id || '0');
    const durationFilter = searchParams.get('duration') || undefined;
    const strategyIdFromQuery = Number(searchParams.get('strategyId'));
    const preservedStrategyQuery = !Number.isNaN(strategyIdFromQuery) && strategyIdFromQuery > 0 ? `strategyId=${strategyIdFromQuery}` : '';
    const backToExperimentsUrl = preservedStrategyQuery ? `/user/experiments?${preservedStrategyQuery}` : '/user/experiments';

    const backToResultsUrl = preservedStrategyQuery
        ? `/user/experimentResults/${experimentId}?${preservedStrategyQuery}`
        : `/user/experimentResults/${experimentId}`;

    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreateListModalOpen, setIsCreateListModalOpen] = useState(false);
    const [isCreateUniverseScreenerModalOpen, setIsCreateUniverseScreenerModalOpen] = useState(false);

    // Fetch entity-level results
    const resultsQuery = useAppQuery(
        {
            ...trpc.experiment.resultsByEntity.queryOptions({
                experimentId,
                duration: durationFilter
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load entity results',
            setError: (msg) => setError(msg)
        }
    );

    // Format profit/loss with color
    const ProfitCell: React.FC<{ value: number | null | undefined }> = ({ value }) => {
        if (value === null || value === undefined) return <span className="text-zinc-500">-</span>;
        const color = value >= 0 ? 'text-green-400' : 'text-red-400';
        return <span className={color}>{value.toFixed(2)}%</span>;
    };

    // Get unique horizons from all entities (sorted with event horizons at end)
    const uniqueHorizons = useMemo(() => {
        if (!resultsQuery.data?.entities) return [];

        const horizonSet = new Set<number>();

        resultsQuery.data.entities.forEach((entity) => {
            Object.keys(entity.horizonMetrics).forEach((horizon) => {
                horizonSet.add(parseInt(horizon));
            });
        });

        // Use sortHorizons to properly order offset horizons first, then event horizons (EOD, NDO)
        return sortHorizons(Array.from(horizonSet)).map((h) => h.toString());
    }, [resultsQuery.data]);

    // Build columns for entity table
    const buildEntityColumns = (includeOptions: boolean = false): ColumnDef<EntityResult>[] => {
        const baseColumns: ColumnDef<EntityResult>[] = [
            {
                id: 'symbol',
                accessorKey: 'symbol',
                header: 'Symbol',
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) => {
                    const entity = row.original;

                    const entityUrl = getEntityLink({
                        entityId: entity.entityId,
                        entityType: entity.entityType
                    });

                    return (
                        <Link className="font-medium text-blue-400 hover:text-blue-300 underline" to={entityUrl}>
                            {entity.symbol}
                        </Link>
                    );
                }
            },
            {
                id: 'entityType',
                accessorKey: 'entityType',
                header: 'Type',
                minSize: 100,
                size: 120,
                maxSize: 140,
                cell: ({ row }) => <Badge variant="secondary">{row.original.entityType}</Badge>
            },
            {
                id: 'totalEntries',
                accessorKey: 'totalEntries',
                header: 'Entries',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <span className="text-white">{row.original.totalEntries}</span>
            },
            {
                id: 'overallAvgProfit',
                accessorKey: 'overallMetrics.avgProfitPercent',
                header: 'Avg Profit %',
                minSize: 100,
                size: 120,
                maxSize: 140,
                cell: ({ row }) => <ProfitCell value={row.original.overallMetrics.avgProfitPercent} />
            },
            {
                id: 'overallAvgSpy',
                accessorKey: 'overallMetrics.avgSpyProfitPercent',
                header: 'SPY %',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <ProfitCell value={row.original.overallMetrics.avgSpyProfitPercent} />
            },
            {
                id: 'overallAvgAlpha',
                accessorKey: 'overallMetrics.avgAlphaPercent',
                header: 'Alpha %',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <ProfitCell value={row.original.overallMetrics.avgAlphaPercent} />
            },
            {
                id: 'overallAvgEntryEdge',
                accessorKey: 'overallMetrics.avgEntryEdgePercent',
                header: () => (
                    <span title="vs random entry into the same name, month, and fixed horizon — shown as avg (covered/eligible); dynamic-exit outcomes use the nearest fixed-horizon proxy, not a replay of the exit logic">
                        Avg Edge %
                    </span>
                ),
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) => (
                    <EdgeCoverageCell
                        coveredCount={row.original.overallMetrics.entryEdgeCoveredCount}
                        coveredEmptyCount={row.original.overallMetrics.entryEdgeCoveredEmptyCount}
                        missingCount={row.original.overallMetrics.entryEdgeMissingCount}
                        value={row.original.overallMetrics.avgEntryEdgePercent}
                    />
                )
            },
            {
                id: 'overallWinRate',
                accessorKey: 'overallMetrics.winRate',
                header: 'Win Rate',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => {
                    const { winRate } = row.original.overallMetrics;
                    if (winRate === null || winRate === undefined) return <span className="text-zinc-500">-</span>;
                    return <span className="text-white">{winRate.toFixed(1)}%</span>;
                }
            },
            {
                id: 'overallWinRateVsSpy',
                accessorKey: 'overallMetrics.winRateVsSpy',
                header: 'Beat SPY %',
                minSize: 90,
                size: 110,
                maxSize: 130,
                cell: ({ row }) => {
                    const { winRateVsSpy } = row.original.overallMetrics;
                    if (winRateVsSpy === null || winRateVsSpy === undefined) return <span className="text-zinc-500">-</span>;
                    const color = winRateVsSpy >= 50 ? 'text-green-400' : 'text-red-400';
                    return <span className={color}>{winRateVsSpy.toFixed(1)}%</span>;
                }
            },
            {
                id: 'bestProfit',
                accessorKey: 'overallMetrics.bestProfit',
                header: 'Best',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <ProfitCell value={row.original.overallMetrics.bestProfit} />
            },
            {
                id: 'worstLoss',
                accessorKey: 'overallMetrics.worstLoss',
                header: 'Worst',
                minSize: 80,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => <ProfitCell value={row.original.overallMetrics.worstLoss} />
            }
        ];

        // Add option columns if experiment includes options
        if (includeOptions) {
            baseColumns.push(
                {
                    id: 'optionEntries',
                    accessorKey: 'overallMetrics.optionTotalEntries',
                    header: 'Completed / Attempts',
                    minSize: 110,
                    size: 130,
                    maxSize: 150,
                    cell: ({ row }) => <OptionOutcomeCompletion evidence={row.original.overallMetrics.optionEvidence} />
                },
                {
                    id: 'optionAvgProfit',
                    accessorKey: 'overallMetrics.optionAvgProfitPercent',
                    header: 'Option Avg %',
                    minSize: 110,
                    size: 130,
                    maxSize: 150,
                    cell: ({ row }) => <ProfitCell value={row.original.overallMetrics.optionAvgProfitPercent} />
                },
                {
                    id: 'optionAvgEntryEdge',
                    accessorKey: 'overallMetrics.avgOptionEntryEdgePercent',
                    header: () => (
                        <span title="option profit vs random entries into the same contract archetype, name, month, and fixed horizon — shown as avg (covered/eligible); dynamic-exit outcomes use the nearest fixed-horizon proxy">
                            Avg Opt Edge %
                        </span>
                    ),
                    minSize: 110,
                    size: 130,
                    maxSize: 160,
                    cell: ({ row }) => (
                        <EdgeCoverageCell
                            coveredCount={row.original.overallMetrics.optionEntryEdgeCoveredCount}
                            coveredEmptyCount={row.original.overallMetrics.optionEntryEdgeCoveredEmptyCount}
                            missingCount={row.original.overallMetrics.optionEntryEdgeMissingCount}
                            value={row.original.overallMetrics.avgOptionEntryEdgePercent}
                        />
                    )
                },
                {
                    id: 'optionWinRate',
                    accessorKey: 'overallMetrics.optionWinRate',
                    header: 'Option Win %',
                    minSize: 100,
                    size: 120,
                    maxSize: 140,
                    cell: ({ row }) => {
                        const { optionWinRate } = row.original.overallMetrics;
                        if (optionWinRate === null || optionWinRate === undefined) return <span className="text-zinc-500">-</span>;
                        return <span className="text-white">{optionWinRate.toFixed(1)}%</span>;
                    }
                },
                {
                    id: 'optionOutperformanceRate',
                    accessorKey: 'overallMetrics.optionOutperformanceRate',
                    header: 'Beat Stock %',
                    minSize: 100,
                    size: 120,
                    maxSize: 140,
                    cell: ({ row }) => {
                        const { optionOutperformanceRate } = row.original.overallMetrics;
                        if (optionOutperformanceRate === null || optionOutperformanceRate === undefined)
                            return <span className="text-zinc-500">-</span>;
                        const color = optionOutperformanceRate >= 50 ? 'text-green-400' : 'text-red-400';
                        return <span className={color}>{optionOutperformanceRate.toFixed(1)}%</span>;
                    }
                },
                {
                    id: 'optionAvgLeverage',
                    accessorKey: 'overallMetrics.optionAvgLeverage',
                    header: 'Avg Leverage',
                    minSize: 100,
                    size: 120,
                    maxSize: 140,
                    cell: ({ row }) => {
                        const { optionAvgLeverage } = row.original.overallMetrics;
                        if (optionAvgLeverage === null || optionAvgLeverage === undefined) return <span className="text-zinc-500">-</span>;
                        return <span className="text-white">{optionAvgLeverage.toFixed(2)}x</span>;
                    }
                }
            );
        }

        // Add columns for each time horizon if there aren't too many
        if (uniqueHorizons.length <= 5 && uniqueHorizons.length > 0) {
            uniqueHorizons.forEach((horizon) => {
                const seconds = parseInt(horizon);
                const label = formatTimeHorizon(seconds);
                const tooltip = isEventHorizon(seconds) ? getEventHorizonTooltip(seconds) : null;

                baseColumns.push({
                    id: `horizon_${horizon}`,
                    header: () => (tooltip ? <span title={tooltip}>{label}</span> : label),
                    minSize: 100,
                    size: 120,
                    maxSize: 140,
                    cell: ({ row }) => {
                        const metrics = row.original.horizonMetrics[horizon];

                        if (!metrics || metrics.totalEntries === 0) {
                            return <span className="text-zinc-500">-</span>;
                        }

                        return (
                            <div className="flex flex-col text-xs">
                                <ProfitCell value={metrics.avgProfitPercent} />
                                <span className="text-zinc-400">{metrics.winRate !== null ? `${metrics.winRate.toFixed(0)}%` : '-'}</span>
                            </div>
                        );
                    }
                });
            });
        }

        return baseColumns;
    };

    // Filter entities based on search term
    const filteredEntities = useMemo((): EntityResult[] => {
        if (!resultsQuery.data?.entities) return [];
        if (!searchTerm) return resultsQuery.data.entities;

        const term = searchTerm.toLowerCase();
        const entities = resultsQuery.data.entities as EntityResult[];
        return entities.filter(
            (entity: EntityResult) => entity.symbol.toLowerCase().includes(term) || entity.entityType.toLowerCase().includes(term)
        );
    }, [resultsQuery.data, searchTerm]);

    // Extract data
    const experiment = resultsQuery.data?.experiment;
    const { isLoading } = resultsQuery;

    // Loading spinner
    useEffect(() => {
        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        return () => hideSpinner();
    }, [isLoading]);

    // Export to CSV
    const handleExportCSV = () => {
        if (filteredEntities.length === 0) return;

        const rows: string[][] = [];
        const includeOptions = experiment?.includeOptions || false;

        // Header row
        const headers = ['Symbol', 'Entity Type', 'Total Entries', 'Avg Profit %', 'Win Rate %', 'Best Profit %', 'Worst Loss %'];

        // Add option headers if experiment includes options
        if (includeOptions) {
            headers.push(
                'Completed / Attempts',
                'Option Avg %',
                'Avg Opt Edge %',
                'Avg Opt Edge Covered',
                'Avg Opt Edge Eligible',
                'Option Win %',
                'Option Beat Stock %',
                'Option Avg Leverage'
            );
        }

        // Add horizon headers (use labels; tooltips are for UI only, not CSV)
        uniqueHorizons.forEach((horizon) => {
            const seconds = parseInt(horizon);
            const label = formatTimeHorizon(seconds);
            headers.push(`${label} Profit %`);
            headers.push(`${label} Win Rate %`);
        });

        rows.push(headers);

        // Data rows
        filteredEntities.forEach((entity) => {
            const row = [
                entity.symbol,
                entity.entityType,
                entity.totalEntries.toString(),
                entity.overallMetrics.avgProfitPercent?.toFixed(2) || '',
                entity.overallMetrics.winRate?.toFixed(1) || '',
                entity.overallMetrics.bestProfit?.toFixed(2) || '',
                entity.overallMetrics.worstLoss?.toFixed(2) || ''
            ];

            // Add option data if experiment includes options
            if (includeOptions) {
                const optionEntriesStr = formatOptionOutcomeCompletion(entity.overallMetrics.optionEvidence);

                row.push(
                    optionEntriesStr,
                    entity.overallMetrics.optionAvgProfitPercent?.toFixed(2) || '',
                    entity.overallMetrics.avgOptionEntryEdgePercent?.toFixed(2) || '',
                    String(entity.overallMetrics.optionEntryEdgeCoveredCount ?? 0),
                    String(
                        computeEdgeEligibleCount({
                            coveredCount: entity.overallMetrics.optionEntryEdgeCoveredCount,
                            coveredEmptyCount: entity.overallMetrics.optionEntryEdgeCoveredEmptyCount,
                            missingCount: entity.overallMetrics.optionEntryEdgeMissingCount
                        })
                    ),
                    entity.overallMetrics.optionWinRate?.toFixed(1) || '',
                    entity.overallMetrics.optionOutperformanceRate?.toFixed(1) || '',
                    entity.overallMetrics.optionAvgLeverage?.toFixed(2) || ''
                );
            }

            // Add horizon data
            uniqueHorizons.forEach((horizon) => {
                const metrics = entity.horizonMetrics[horizon];

                if (metrics && metrics.totalEntries > 0) {
                    row.push(metrics.avgProfitPercent?.toFixed(2) || '');
                    row.push(metrics.winRate?.toFixed(1) || '');
                } else {
                    row.push('');
                    row.push('');
                }
            });

            rows.push(row);
        });

        const csv = rows.map((row) => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `experiment-${experimentId}-entities${durationFilter ? `-${durationFilter}` : ''}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <UserLayout title="Entity Results - Project 314">
                <DataLoadingSpinner text="Loading entity results..." />
            </UserLayout>
        );
    }

    if (error) {
        return (
            <UserLayout title="Entity Results - Error - Project 314">
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            </UserLayout>
        );
    }

    if (!experiment) {
        return (
            <UserLayout title="Experiment Not Found - Project 314">
                <div className="container mx-auto">
                    <div className="text-center py-12">
                        <h3 className="text-white text-xl mb-4">Experiment not found</h3>
                        <Button variant="secondary" onClick={() => navigate(backToExperimentsUrl)}>
                            Back to Experiments
                        </Button>
                    </div>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1600px" title={`Entity Results - ${experiment.name} - Project 314`}>
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="flex justify-between items-start mb-6 gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <Button
                                className="flex items-center whitespace-nowrap"
                                size="sm"
                                variant="secondary"
                                onClick={() => navigate(backToResultsUrl)}
                            >
                                <FaArrowLeft className="inline-block flex-shrink-0 mr-2" />
                                <span className="inline-block">Back to Results</span>
                            </Button>
                            <h3 className="text-white text-xl font-semibold">{experiment.name}</h3>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                            <Badge variant={getExecutionStatusBadgeVariant(experiment.executionStatus)}>
                                Execution: {experiment.executionStatus}
                            </Badge>
                            {experiment.discoveryStatus !== 'disabled' && (
                                <Badge variant={getDiscoveryStatusBadgeVariant(experiment.discoveryStatus)}>
                                    Discovery: {experiment.discoveryStatus}
                                </Badge>
                            )}
                            {durationFilter && <Badge variant="info">Duration: {durationFilter}</Badge>}
                            {experiment.completedAt && (
                                <span className="text-zinc-500 text-sm">
                                    Completed {dayjs(experiment.completedAt).format('MMM D, YYYY h:mm A')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                        {durationFilter && (
                            <Button
                                className="flex items-center whitespace-nowrap"
                                variant="secondary"
                                onClick={() =>
                                    navigate(
                                        preservedStrategyQuery
                                            ? `/user/experimentResults/${experimentId}/entities?${preservedStrategyQuery}`
                                            : `/user/experimentResults/${experimentId}/entities`
                                    )
                                }
                            >
                                Clear Filter
                            </Button>
                        )}
                        <Button
                            className="flex items-center whitespace-nowrap"
                            disabled={filteredEntities.length === 0}
                            variant="primary"
                            onClick={() => setIsCreateListModalOpen(true)}
                        >
                            <FaList className="inline-block flex-shrink-0 mr-2" />
                            <span className="inline-block">Create Entity List</span>
                        </Button>
                        <Button
                            className="flex items-center whitespace-nowrap"
                            disabled={filteredEntities.length === 0}
                            variant="primary"
                            onClick={() => setIsCreateUniverseScreenerModalOpen(true)}
                        >
                            <FaFilter className="inline-block flex-shrink-0 mr-2" />
                            <span className="inline-block">Create Universe Screener</span>
                        </Button>
                        <Button
                            className="flex items-center whitespace-nowrap"
                            disabled={filteredEntities.length === 0}
                            variant="secondary"
                            onClick={handleExportCSV}
                        >
                            <FaDownload className="inline-block flex-shrink-0 mr-2" />
                            <span className="inline-block">Export CSV</span>
                        </Button>
                    </div>
                </div>

                {/* Summary Card */}
                <Card className="bg-zinc-900 mb-6">
                    <div className="grid grid-cols-4 gap-6">
                        <div>
                            <div className="text-zinc-400 text-sm mb-1">Total Entities</div>
                            <div className="text-white text-lg font-semibold">{resultsQuery.data?.totalEntities || 0}</div>
                        </div>
                        <div>
                            <div className="text-zinc-400 text-sm mb-1">Date Range</div>
                            <div className="text-white">
                                {experiment.dateRange && (
                                    <>
                                        {dayjs(experiment.dateRange.start).format('MMM D, YYYY')} -{' '}
                                        {dayjs(experiment.dateRange.end).format('MMM D, YYYY')}
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400 text-sm mb-1">Time Horizons</div>
                            <div className="text-white">{uniqueHorizons.length} horizons</div>
                        </div>
                        <div>
                            <div className="text-zinc-400 text-sm mb-1">View Mode</div>
                            <div className="text-white">By Entity {durationFilter && `(${durationFilter})`}</div>
                        </div>
                    </div>
                </Card>

                {/* Search Bar */}
                <Card className="bg-zinc-900 mb-6">
                    <div className="flex items-center gap-4">
                        <FaSearch className="text-zinc-400" />
                        <FormInput
                            className="flex-1"
                            placeholder="Search by symbol or type..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <Button size="sm" variant="secondary" onClick={() => setSearchTerm('')}>
                                Clear
                            </Button>
                        )}
                    </div>
                </Card>

                {/* Entity Results Table */}
                <Card className="bg-zinc-900">
                    {filteredEntities.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-zinc-400">{searchTerm ? 'No entities match your search.' : 'No results available.'}</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <h4 className="text-white font-semibold">Entity Performance ({filteredEntities.length} entities)</h4>
                                {uniqueHorizons.length > 5 && (
                                    <p className="text-zinc-400 text-sm mt-1">
                                        Showing overall metrics. Individual horizon data available in CSV export.
                                    </p>
                                )}
                            </div>
                            <DataTable
                                sort
                                striped
                                columns={buildEntityColumns(experiment?.includeOptions || false)}
                                data={filteredEntities}
                                pagination={{
                                    enabled: true,
                                    pageSize: 25
                                }}
                                search={false}
                            />
                        </>
                    )}
                </Card>
            </div>

            {/* Create Entity List Modal */}
            <CreateEntityListModal
                duration={durationFilter}
                experimentId={experimentId}
                experimentName={experiment?.name || ''}
                isOpen={isCreateListModalOpen}
                onClose={() => setIsCreateListModalOpen(false)}
                onSuccess={() => {
                    setIsCreateListModalOpen(false);
                    // Stay on the current page - the toast will show the success message
                }}
            />

            <CreateUniverseScreenerModal
                duration={durationFilter}
                experimentId={experimentId}
                experimentName={experiment?.name || ''}
                isOpen={isCreateUniverseScreenerModalOpen}
                onClose={() => setIsCreateUniverseScreenerModalOpen(false)}
                onSuccess={() => {
                    setIsCreateUniverseScreenerModalOpen(false);
                }}
            />
        </UserLayout>
    );
};

export default ExperimentEntityResults;
